import { TournamentAnalysisHeader } from "@/components/general/TournamentAnalysis/TournamentAnalysisHeader";
import { useQuery } from "@tanstack/react-query";
import { TournamentMatchesXg, tournamentMatchesXgQueryOptions, tournamentsQueryOptions } from "@/api/tournaments";
import { useState, useEffect, createContext, useContext } from "react";
import { Outlet } from "@tanstack/react-router";
import type { Tournament } from "@/api/tournaments";
import TournamentAnalysisNavigation from "@/components/general/TournamentAnalysis/TournamentAnalysisNavigation";
import type { StatCategory } from "@/components/general/TournamentAnalysis/TournamentAnalysisNavigation";
import { useSelectedTournament } from "@/context/tournamentContext";
import { useSelectedTeam } from "@/context/teamContext";

interface TournamentContextValue {
    selectedTournament: string;
    setSelectedTournament: (tournament: string) => void;
    selectedTournamentId: number | undefined;
    tournaments: Tournament[] | undefined;
    viewMode: "average" | "total";
    setViewMode: (mode: "average" | "total") => void;
    selectedPlayers: Array<{ playerId: number; firstName: string; lastName: string }>;
    setSelectedPlayers: (players: Array<{ playerId: number; firstName: string; lastName: string }>) => void;
    statCategory: StatCategory;
    setStatCategory: (category: StatCategory) => void;
    matchXgs: TournamentMatchesXg | undefined;
}

const TournamentContext = createContext<TournamentContextValue | undefined>(undefined);

export const useTournamentContext = () => {
    const context = useContext(TournamentContext);
    if (!context) {
        throw new Error("useTournamentContext must be used within a TournamentProvider");
    }
    return context;
};

export default function TournamentAnalysisPage() {
    return (
        <TournamentProvider>
            <TournamentContent />
        </TournamentProvider>
    );
}

function TournamentProvider({ children }: { children: React.ReactNode }) {
    const { tournamentId: globalTournamentId, setTournamentId: setGlobalTournamentId } = useSelectedTournament();
    const { teamId } = useSelectedTeam();
    const { data: tournaments } = useQuery(tournamentsQueryOptions(teamId));
    const [selectedTournament, setSelectedTournament] = useState("");
    const [viewMode, setViewMode] = useState<"average" | "total">("average");
    const [selectedPlayers, setSelectedPlayers] = useState<
        Array<{ playerId: number; firstName: string; lastName: string }>
    >([]);
    const [statCategory, setStatCategory] = useState<StatCategory>("overview");

    const mostRecentTournament = tournaments
        ?.filter((tournament) => tournament.matches && tournament.matches.length > 0)
        ?.reduce(
            (latest, current) => (latest == undefined || current.id > latest.id ? current : latest),
            undefined as unknown as Tournament
        );

    useEffect(() => {
        if (globalTournamentId && tournaments) {
            const globalTournament = tournaments.find((t) => t.id === globalTournamentId);
            if (globalTournament && selectedTournament === "") {
                setSelectedTournament(globalTournament.name);
            }
        } else if (mostRecentTournament && selectedTournament === "") {
            setSelectedTournament(mostRecentTournament.name);
        }
    }, [mostRecentTournament, selectedTournament, globalTournamentId, tournaments]);

    const selectedTournamentId = tournaments?.find((tournament) => tournament.name === selectedTournament)?.id;

    useEffect(() => {
        if (selectedTournamentId) {
            setGlobalTournamentId(selectedTournamentId);
        }
    }, [selectedTournamentId, setGlobalTournamentId]);

    const { data: matchesXgData } = useQuery(tournamentMatchesXgQueryOptions(selectedTournamentId, teamId));

    const value = {
        selectedTournament,
        setSelectedTournament,
        selectedTournamentId,
        tournaments,
        viewMode,
        setViewMode,
        selectedPlayers,
        setSelectedPlayers,
        statCategory,
        setStatCategory,
        matchXgs: matchesXgData,
    };

    return <TournamentContext.Provider value={value}>{children}</TournamentContext.Provider>;
}

function TournamentContent() {
    const {
        selectedTournament,
        setSelectedTournament,
        viewMode,
        setViewMode,
        selectedTournamentId,
        tournaments,
        selectedPlayers,
        statCategory,
        setStatCategory,
    } = useTournamentContext();

    return (
        <main data-tournament-loaded={selectedTournamentId ? "true" : undefined} className="flex flex-col p-4">
            <div id="onboarding-tournament-header">
                <TournamentAnalysisHeader
                    selectedTournament={selectedTournament}
                    onTournamentChange={setSelectedTournament}
                    selectedTournamentId={selectedTournamentId}
                    tournaments={tournaments}
                />
            </div>
            <TournamentAnalysisNavigation
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                selectedTournamentId={selectedTournamentId}
                selectedPlayers={selectedPlayers}
                statCategory={statCategory}
                onStatCategoryChange={setStatCategory}
            />
            <div className="mt-2">
                <Outlet />
            </div>
            <div className="lg:hidden py-7.5" />
        </main>
    );
}
